import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

// `[[record-id]]` (same audience) and `[[audience/record-id]]` (ADR 0023).
// Kept in step with _WIKILINK in src/brain/okf/links.py — pipes excluded so a
// future [[id|label]] form cannot have its label swallowed into the id.
const WIKILINK = /\[\[([^[\]|]+?)\]\]/g

/**
 * Rewrite wikilinks to real markdown links before rendering.
 *
 * Done as a text pass rather than a remark plugin because the target is always
 * the same console route: record ids are articulated, immutable slugs (ADR
 * 0014), so `[[b1-fund-size]]` already contains everything the href needs and
 * there is nothing to resolve against a link table.
 *
 * The link text stays the id. It reads as the fact it points to — which is the
 * whole reason ADR 0014 made ids articulated — and it cannot go stale the way a
 * cached copy of the target's title would after a retitle.
 */
function linkify(content: string, audience: string): string {
  return content.replace(WIKILINK, (whole, raw: string) => {
    const target = raw.trim()
    if (!target) return whole
    const [linkAudience, recordId] = target.includes("/")
      ? [target.slice(0, target.indexOf("/")).trim(), target.slice(target.indexOf("/") + 1).trim()]
      : [audience, target]
    if (!linkAudience || !recordId) return whole
    return `[${recordId}](/records/${encodeURIComponent(linkAudience)}/${encodeURIComponent(recordId)})`
  })
}

export function MarkdownView({ content, audience }: { content: string; audience?: string }) {
  // Without an audience a bare [[id]] has nothing to resolve against, so leave
  // the raw text alone rather than inventing a link that 404s.
  const rendered = audience ? linkify(content, audience) : content
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none font-sans">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{rendered}</ReactMarkdown>
    </div>
  )
}
